/**
 * usePhdHistory - Manages simulation history CRUD and current result state.
 *
 * Fixes D7: proper useCallback/useEffect dependency handling for loadFullSimulation.
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import {
    getSimulationHistory,
    getSimulation,
    saveSimulation,
    deleteSimulation,
} from '@vortx/shared-ui';

export function usePhdHistory(currentUser, projectId) {
    const [storedSimulations, setStoredSimulations] = useState([]);
    const [loadingHistory, setLoadingHistory] = useState(true);
    const [simulationResult, setSimulationResult] = useState(null);
    const [comparisonResults, setComparisonResults] = useState(null);

    // Track whether initial load has set a result, to avoid re-loading
    const hasLoadedInitial = useRef(false);

    // Reset initial load flag when projectId changes (e.g. switching projects)
    useEffect(() => {
        hasLoadedInitial.current = false;
        setStoredSimulations([]);
        setSimulationResult(null);
        setComparisonResults(null);
        setLoadingHistory(true);
    }, [projectId]);

    // Load full simulation results from API
    const loadFullSimulation = useCallback(async (simulationId) => {
        try {
            const data = await getSimulation(simulationId, currentUser);
            setSimulationResult(data.results);
            setComparisonResults(null);
            return data;
        } catch (error) {
            console.error('Failed to load simulation:', error);
        }
        return null;
    }, [currentUser]);

    // Fetch simulation history on mount / user change
    useEffect(() => {
        const fetchHistory = async () => {
            if (!currentUser) {
                setLoadingHistory(false);
                return;
            }

            try {
                const rawSimulations = await getSimulationHistory(10, currentUser, projectId);
                const simulations = rawSimulations.map(sim => ({
                    id: sim.simulation_id,
                    timestamp: sim.created_at,
                    topology: {
                        id: sim.topology_id,
                        name: sim.topology_name,
                    },
                    metadata: sim,
                    result: null,
                }));
                setStoredSimulations(simulations);

                // Load most recent if no result loaded yet
                if (simulations.length > 0 && !hasLoadedInitial.current) {
                    hasLoadedInitial.current = true;
                    await loadFullSimulation(simulations[0].id);
                }
            } catch (error) {
                console.error('Failed to fetch simulation history:', error);
            } finally {
                setLoadingHistory(false);
            }
        };

        fetchHistory();
    }, [currentUser, projectId, loadFullSimulation]);

    // Save a simulation result to history
    const handleSaveSimulation = useCallback(async (config, result, selectedTopology) => {
        if (!currentUser) return;

        try {
            const fullConfig = { ...config, topology_id: selectedTopology?.id };
            const data = await saveSimulation(fullConfig, result, currentUser, projectId);
            const newSimulation = {
                id: data.simulation_id,
                timestamp: data.metadata.created_at,
                topology: selectedTopology,
                metadata: data.metadata,
                result: result,
            };
            setStoredSimulations(prev => [newSimulation, ...prev].slice(0, 10));
        } catch (error) {
            console.error('Failed to save simulation:', error);
        }
    }, [currentUser, projectId]);

    // Load a simulation from history
    const handleLoadSimulation = useCallback(async (simulation) => {
        if (simulation.result) {
            setSimulationResult(simulation.result);
            setComparisonResults(null);
            return simulation;
        }
        return loadFullSimulation(simulation.id);
    }, [loadFullSimulation]);

    // Delete a simulation from history
    const handleDeleteSimulation = useCallback(async (simulationId) => {
        if (!currentUser) return;

        try {
            await deleteSimulation(simulationId, currentUser);
            const updated = storedSimulations.filter(s => s.id !== simulationId);
            setStoredSimulations(updated);

            if (updated.length > 0) {
                await loadFullSimulation(updated[0].id);
            } else {
                setSimulationResult(null);
            }
        } catch (error) {
            console.error('Failed to delete simulation:', error);
        }
    }, [currentUser, storedSimulations, loadFullSimulation]);

    return {
        storedSimulations,
        loadingHistory,
        simulationResult,
        comparisonResults,
        setSimulationResult,
        setComparisonResults,
        handleSaveSimulation,
        handleLoadSimulation,
        handleDeleteSimulation,
    };
}
